import React, { useMemo, useRef } from "react";
import { Route, Redirect, useLocation } from "react-router";
import { useSelector } from "react-redux";
import { IonPage, IonContent, IonSpinner } from "@ionic/react";
import { isOnboardingIncomplete, onboardingResumePath } from "../onboardingProfile";
import { cleanStaleAuthData } from "../authGuard";
import { getAuthToken, readAuth } from "../authStorage";
import { RECIPE_PATH_RE, savePendingCommunityPath } from "../../utils/communityDeepLinks";
import { recipePathWithOpenAccess } from "../../utils/recipeOpenAccess";

const safeJson = (s, fallback) => {
  try {
    return JSON.parse(s ?? "") ?? fallback;
  } catch {
    return fallback;
  }
};

const isPublicOnlyPath = (p) =>
  p === "/" || p === "/login" || p.startsWith("/o/");

const requiredForPath = (pathname) => {
  const p = pathname ?? window.location.pathname;
  if (isPublicOnlyPath(p)) return "public";
  if (p.startsWith("/impostazioni-iniziali")) return "pre";
  return "full";
};

const parseOnboardingStep = (pathname) => {
  const m = String(pathname || "").match(/\/impostazioni-iniziali\/(\d+)/);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) ? n : null;
};

// merge without overwriting with null/undefined
const mergeDefined = (base, patch) => {
  const out = { ...(base || {}) };
  Object.entries(patch || {}).forEach(([k, v]) => {
    if (v !== undefined && v !== null) out[k] = v;
  });
  return out;
};

/** kept export (other files import it) */
export const getAuthPath = (pathname) => requiredForPath(pathname);

const readStoredProfile = () => safeJson(readAuth("adoc_p"), null);

/**
 * A support session opened from the CRM.
 *
 * Onboarding is a gate for the member who owns the account, not for the operator
 * looking at it: an unfinished profile makes every protected route redirect to
 * step 1, so support could never reach the very accounts they are asked about —
 * and answering that wizard would write the operator's data into the member's
 * profile. While impersonating, onboarding is treated as satisfied.
 */
const isImpersonating = () => {
  try {
    return readAuth("adoc_i") === "true";
  } catch {
    return false;
  }
};

/** kept exports (other files import these) */
export const userAuthStatus = () => {
  // Clear stale tokens before checking status
  cleanStaleAuthData();

  const hasBearer = !!getAuthToken();
  const hasRegToken = !!localStorage.getItem("adoc_rt");
  const pending = hasRegToken && !hasBearer;

  if (pending) return "pre";
  if (!hasBearer) return "none";

  const stored = readStoredProfile();

  // token exists but profile not loaded yet -> treat as "pre" (app will show loader via RouteAuth gate)
  if (!stored) return "pre";

  const hasUser = !!(
    stored?.user?.id ||
    stored?.user?.email ||
    stored?.master_club_subscriber?.id
  );
  if (!hasUser) return "pre";

  if (isImpersonating()) return "full";

  return isOnboardingIncomplete(stored) ? "pre" : "full";
};

export const isFullAuth = () => userAuthStatus() === "full";
export const isPreAuth = () => userAuthStatus() === "pre";

const LoadingPage = () => (
  <IonPage>
    <IonContent fullscreen className="ion-padding">
      <div
        style={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          height: "100%",
          justifyContent: "center",
        }}
      >
        <IonSpinner />
      </div>
    </IonContent>
  </IonPage>
);

const RouteAuth = ({ component: Component, ...rest }) => {
  const location = useLocation();
  const required = requiredForPath(location.pathname);

  // Clean stale/mismatched tokens before reading auth state
  cleanStaleAuthData();

  const tokenRedux = useSelector((s) => s.user?.token);
  const me = useSelector((s) => s.user?.user);
  const subscriber = useSelector((s) => s.user?.master_club_subscriber);

  // IMPORTANT: use only the bootstrap loading flag, not generic loading
  const authBootLoading = useSelector((s) => !!s.user?.getUserIsLoading);

  const hasBearer = !!tokenRedux || !!getAuthToken();
  const hasRegToken = !!localStorage.getItem("adoc_rt");
  const pending = hasRegToken && !hasBearer;

  const storedProfile = safeJson(readAuth("adoc_p"), null);

  const profileForCheck = useMemo(() => {
    const storedU = storedProfile?.user ?? {};
    const storedMc = storedProfile?.master_club_subscriber ?? {};

    const mergedMc = mergeDefined(storedMc, subscriber);
    let mergedUser = mergeDefined(storedU, me);

    if (mergedMc?.user) mergedUser = mergeDefined(mergedUser, mergedMc.user);

    return { user: mergedUser, master_club_subscriber: mergedMc };
  }, [me, subscriber, storedProfile]);

  const hasUser =
    !!profileForCheck?.user?.id ||
    !!profileForCheck?.user?.email ||
    !!profileForCheck?.master_club_subscriber?.id;

  // Support must not fill in a member's onboarding: the answers would be written
  // into the member's own profile. The whole check is skipped while impersonating.
  const impersonating = isImpersonating();

  const incomplete = hasUser
    ? !impersonating && isOnboardingIncomplete(profileForCheck)
    : true;

  const status = useMemo(() => {
    if (pending) return "pre";
    if (!hasBearer) return "none";
    if (!hasUser) return "pre";
    return incomplete ? "pre" : "full";
  }, [pending, hasBearer, hasUser, incomplete]);

  // An unfinished onboarding continues at its first incomplete screen. A pending
  // registration (older app builds) always starts at step 1.
  const resumePath = pending
    ? "/impostazioni-iniziali/1"
    : onboardingResumePath(profileForCheck);

  // ---------- NO-FLASH GATE ----------
  // If token exists but we don't have enough profile yet, show loader instead of redirecting to step 1.
  const profileLoaded = !!storedProfile || !!me || !!subscriber;

  const resolvingRef = useRef(0);
  const needsResolve = hasBearer && !pending && (!profileLoaded || !hasUser);

  if (needsResolve && !resolvingRef.current) resolvingRef.current = Date.now();
  if (!needsResolve && resolvingRef.current) resolvingRef.current = 0;

  const resolveAge = resolvingRef.current
    ? Date.now() - resolvingRef.current
    : 0;
  // A support session keeps waiting instead of falling through. When the profile
  // is slow or fails, the 2.5s window expires, `hasUser` is still false, and a
  // protected route redirects to onboarding step 1 — the one place an operator
  // must never reach, because the answers would be written into the member's own
  // profile. A loader is the safe thing to show until the account arrives.
  const blockRedirects = needsResolve && (impersonating || resolveAge < 2500);
  // ---------------------------------

  return (
    <Route
      {...rest}
      render={(p) => {
        // /logout must always be reachable so users can clear their
        // session regardless of current auth state. Without this,
        // requiredForPath() returns "full" for /logout and a mid-
        // onboarding (pre-auth) user gets bounced to
        // /impostazioni-iniziali/1 instead of LogoutRoute mounting —
        // which is exactly the loop the onboarding force-quit guard
        // triggers (cold-start redirect to /logout silently bounced
        // back into onboarding, token + profile never cleared, next
        // force-quit does the same thing forever).
        if (location.pathname === "/logout") {
          return <Component {...p} />;
        }

        // Only block with a full-page loader when we truly cannot decide auth yet.
        // (token exists but we still don't have any profile/user identity loaded)
        const bootstrapMissingProfile =
          hasBearer && !pending && (!profileLoaded || !hasUser);

        // Brief window inside commitPendingRegistration where we've already
        // written the bearer to localStorage but haven't yet cleared
        // adoc_rt — that happens only after getUserInfo resolves. During
        // that window, getUserInfo's .pending action flips
        // getUserIsLoading=true and we'd otherwise render LoadingPage,
        // unmounting PublicProfileStep, dismissing its overlay, and
        // remounting it fresh with SUCCESSIVO re-enabled (the reported
        // flash on first signup). PublicProfileStep is still mounted
        // beneath, driving the persistAll sequence with its own overlay
        // — let it keep rendering until the transition completes.
        const isCommittingRegistration = hasBearer && hasRegToken;

        if (
          bootstrapMissingProfile &&
          (authBootLoading || blockRedirects) &&
          !isCommittingRegistration
        ) {
          return <LoadingPage />;
        }

        if (location.pathname === "/onboarding-complete") {
          if (status === "none") return <Redirect to="/login" />;
          return <Component {...p} />;
        }

        // Pending registration: allow only step 1 & 2
        if (pending) {
          const step = parseOnboardingStep(location.pathname);
          if (step != null && step > 2)
            return <Redirect to="/impostazioni-iniziali/2" />;
        }

        // PUBLIC routes
        if (required === "public") {
          if (status === "none") return <Component {...p} />;
          return (
            <Redirect
              to={status === "pre" ? resumePath : "/home"}
            />
          );
        }

        // ONBOARDING routes
        // ONBOARDING routes
        if (required === "pre") {
          if (status === "pre") return <Component {...p} />;

          // ✅ if onboarding just became complete, go to the "program ready" screen.
          // `fromOnboarding` because that is what happened: the refreshed profile
          // lands here BEFORE the last step's own navigation, and an arrival
          // without it sent a member who had just finished onboarding to /home
          // instead of the Free Programme screen.
          if (status === "full") {
            return (
              <Redirect
                to={{
                  pathname: "/onboarding-complete",
                  state: { fromOnboarding: true },
                }}
              />
            );
          }

          return <Redirect to="/login" />;
        }

        // FULL routes
        // Keep ?src=magazine so a cold open from Magazine still unlocks after
        // the login redirect (same skip as the home recipe widget).
        if (status !== "full" && RECIPE_PATH_RE.test(location.pathname)) {
          savePendingCommunityPath(
            recipePathWithOpenAccess(
              location.pathname,
              new URLSearchParams(location.search || ""),
            ),
          );
        }
        if (status === "full") return <Component {...p} />;
        return (
          <Redirect
            to={status === "none" ? "/login" : resumePath}
          />
        );
      }}
    />
  );
};

export default RouteAuth;
